import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WorkflowDto } from 'src/generated/services/TFinancialApi';
import Button from 'src/presentations/components/atom/button';
import { useUpsertWorkflow } from 'src/use-cases/settings/useWorkflows';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { FormContainer } from 'src/presentations/components/organisms/forms/FormContainer';
import { WorkflowTextField, WorkflowTextArea } from './WorkflowRuleFields';

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflow?: WorkflowDto | null;
  onSuccess?: () => void;
}

interface WorkflowFormValues {
  name: string;
  description: string;
  enabled: boolean;
}

export const WorkflowModal: FC<WorkflowModalProps> = ({ isOpen, onClose, workflow, onSuccess }) => {
  const { mutateAsync: upsertWorkflow, isLoading } = useUpsertWorkflow();

  const methods = useForm<WorkflowFormValues>({
    defaultValues: {
      name: '',
      description: '',
      enabled: true,
    },
  });

  const { handleSubmit, reset } = methods;

  // Reset form when modal opens/closes or workflow changes
  useEffect(() => {
    if (isOpen) {
      if (workflow) {
        reset({
          name: workflow.name || '',
          description: workflow.description || '',
          enabled: workflow.enabled ?? true,
        });
      } else {
        reset({
          name: '',
          description: '',
          enabled: true,
        });
      }
    }
  }, [isOpen, workflow, reset]);

  const onSubmit = async (data: WorkflowFormValues) => {
    try {
      const workflowData: WorkflowDto = {
        id: workflow?.id,
        name: data.name,
        description: data.description,
        enabled: data.enabled,
      };

      await upsertWorkflow(workflowData);
      openToast({
        title: 'Success',
        description: workflow ? 'Workflow updated successfully' : 'Workflow created successfully',
        toastType: 'success',
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      openToastError(error as Error);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const isEditMode = !!workflow;

  const getSubmitButtonText = () => {
    if (isLoading) return 'Saving...';
    if (isEditMode) return 'Save';
    return 'Create';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '24px',
        },
      }}
    >
      <DialogTitle sx={{ padding: 0, marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className='text-18 font-semibold'>{isEditMode ? 'Edit Workflow' : 'Add New Workflow'}</span>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <FormContainer formContext={methods}>
          <div className='space-y-20'>
            <WorkflowTextField name='name' label='Workflow Name' placeholder='Enter workflow name' required />

            <WorkflowTextArea name='description' label='Description' placeholder='Enter workflow description' rows={4} />

            <div className='flex gap-12 pt-20'>
              <Button variant='secondary' className='flex-1' onClick={handleCancel} disabled={isLoading} type='button'>
                Cancel
              </Button>
              <Button variant='primary' className='flex-1' onClick={handleSubmit(onSubmit)} disabled={isLoading} type='button'>
                {getSubmitButtonText()}
              </Button>
            </div>
          </div>
        </FormContainer>
      </DialogContent>
    </Dialog>
  );
};
